/**
 * salaryProcessApi.js
 * --------------------
 * API client and report configuration for Salary Process (Payroll Engine).
 *
 * Strictly mirrors legacy DAL and BLL:
 * - DAL/PayRoll/SalaryProcessDAL.cs
 * - DAL/PayRoll/PayrollProcessDAL.cs
 * - BLL/PayRoll/PayRollProcessBLL.cs
 */
import api from '../../../../api/client';
import {
  MONTH_OPTIONS,
  YEAR_OPTIONS,
  GENDER_OPTIONS,
  STATUS_OPTIONS,
  STANDARD_PAYROLL_TOOLS,
  getMonthName,
} from '../../common/payrollDateUtils';

export {
  MONTH_OPTIONS,
  YEAR_OPTIONS,
  GENDER_OPTIONS,
  STATUS_OPTIONS,
  STANDARD_PAYROLL_TOOLS,
  getMonthName,
};

// ---------------------------------------------------------------------------
// SALARY PROCESS TOOLS (mirrors legacy SalaryProcessToolsMenu.jsx)
// ---------------------------------------------------------------------------
export const SALARY_PROCESS_TOOLS = [
  { key: 'upload', label: 'Upload File To Process Salary', icon: 'cloud_upload' },
  { key: 'journal', label: 'Journal', icon: 'currency_rupee' },
  { key: 'payment', label: 'Payment', icon: 'payments' },
  { key: 'rollback', label: 'RollBack Process', icon: 'history', danger: true },
  { key: 'payslip', label: 'Payslip', icon: 'receipt_long' },
  { key: 'print', label: 'Print Report', icon: 'print' },
  { key: 'format', label: 'Salary Slip Format', icon: 'description' },
  { key: 'export_excel', label: 'Export Excel', icon: 'table_view' },
  { key: 'export_pdf', label: 'Export PDF', icon: 'picture_as_pdf' },
];

// ---------------------------------------------------------------------------
// 4 PREDEFINED REPORTS (exact 4 reports shown in frontend UI)
// ---------------------------------------------------------------------------
export const SALARY_PREDEFINED_REPORTS = [
  {
    reportKey: 'paysheet_report',
    title: 'Employee Paysheet Report',
    description:
      'Comprehensive monthly paysheet per employee — absent days, present days, total earnings, total deductions, and net amount.',
    defaultColumns: [
      'Particulars',
      'Absent Days',
      'Present Days',
      'Total Earnings',
      'Total Deductions',
      'Net Amount',
    ],
  },
  {
    reportKey: 'pf_report',
    title: 'PF Report',
    description:
      'Provident Fund deduction and employer contribution report by employee and pay head.',
    defaultColumns: ['Employee Name', 'Pay Head', 'Amount'],
  },
  {
    reportKey: 'esic_report',
    title: 'ESIC Report',
    description: 'Employees State Insurance Corporation contribution report.',
    defaultColumns: ['Particulars', 'Pay Heads', 'Amount'],
  },
  {
    reportKey: 'pt_report',
    title: 'Professional Tax Monthly Report',
    description:
      'Monthly Professional Tax (PT) deduction report with worked days and gross wages.',
    defaultColumns: [
      'Emp ID',
      'Employee Name',
      'Month',
      'Worked Days',
      'Gross Wages',
      'PT Amount',
    ],
  },
];

export const SALARY_REPORT_COLUMNS_MAP = {
  paysheet_report: [
    { key: 'Particulars', label: 'Particulars', align: 'left' },
    { key: 'AbsentDays', label: 'Absent Days', align: 'right' },
    { key: 'PresentDays', label: 'Present Days', align: 'right' },
    { key: 'TotalEarnings', label: 'Total Earnings', align: 'right' },
    { key: 'TotalDeductions', label: 'Total Deductions', align: 'right' },
    { key: 'NetAmount', label: 'Net Amount', align: 'right' },
  ],
  pf_report: [
    { key: 'EmployeeName', label: 'Employee Name', align: 'left' },
    { key: 'PayHead', label: 'Pay Head', align: 'left' },
    { key: 'Amount', label: 'Amount', align: 'right' },
  ],
  esic_report: [
    { key: 'Particulars', label: 'Particulars', align: 'left' },
    { key: 'PayHeads', label: 'Pay Heads', align: 'left' },
    { key: 'Amount', label: 'Amount', align: 'right' },
  ],
  pt_report: [
    { key: 'EmpID', label: 'Emp ID', align: 'left' },
    { key: 'EmployeeName', label: 'Employee Name', align: 'left' },
    { key: 'Month', label: 'Month', align: 'left' },
    { key: 'WorkedDays', label: 'Worked Days', align: 'right' },
    { key: 'GrossWages', label: 'Gross Wages', align: 'right' },
    { key: 'PTAmount', label: 'PT Amount', align: 'right' },
  ],
  esic_challan: [
    { key: 'IP Number', label: 'IP Number', align: 'left' },
    { key: 'IP Name', label: 'IP Name', align: 'left' },
    { key: 'Wages Paid Days', label: 'Wages Paid Days', align: 'right' },
    { key: 'Total Monthly Wages', label: 'Total Monthly Wages', align: 'right' },
  ],
  pf_challan: [
    { key: 'UAN', label: 'UAN', align: 'left' },
    { key: 'Member Name', label: 'Member Name', align: 'left' },
    { key: 'Gross Wages', label: 'Gross Wages', align: 'right' },
    { key: 'EPF Wages', label: 'EPF Wages', align: 'right' },
    { key: 'EPS Wages', label: 'EPS Wages', align: 'right' },
    { key: 'EDLI Wages', label: 'EDLI Wages', align: 'right' },
    { key: 'EPF Contri Remitted', label: 'EPF Contri Remitted', align: 'right' },
    { key: 'NC PDays', label: 'NC PDays', align: 'right' },
  ],
};

function mapSalaryItem(r) {
  return {
    id: r.id || r.ID,
    month: r.month || r.Month || '',
    idCard: r.id_card_no || r.IDCardNo || '',
    name: r.emp_name || r.Name || '',
    empId: r.emp_id || r.EMPID || 0,
    totalEarnings: Number(r.total_earnings ?? r.TotalEarnings ?? 0),
    totalDeductions: Number(r.total_deductions ?? r.TotalDeductions ?? 0),
    netAmount: Number(r.net_amount ?? r.NetAmount ?? 0),
    status: r.status || 'Processed',
    companyName: r.company_name || r.Company || '',
    branchName: r.branch_name || r.Branch || '',
    departmentName: r.department_name || r.Department || '',
  };
}

export const salaryProcessApi = {
  /** Dynamic dropdown options for SharedFilters */
  getFilterOptions: async () => {
    try {
      const { data } = await api.get('/payroll/salary-process/options');
      return data;
    } catch {
      return {
        companies: [],
        branches: [],
        departments: [],
        groups: [],
        genders: GENDER_OPTIONS,
      };
    }
  },

  /** Paginated list of processed salary records */
  getRecords: async (filters = {}) => {
    const params = {
      month: filters.month ?? new Date().getMonth() + 1,
      year: filters.year ?? new Date().getFullYear(),
      page: filters.page ?? 1,
      page_size: filters.pageSize ?? 20,
    };
    if (filters.companyId && filters.companyId > 0) params.company_id = filters.companyId;
    if (filters.branchId && filters.branchId > 0) params.branch_id = filters.branchId;
    if (filters.deptId && filters.deptId > 0) params.dept_id = filters.deptId;
    if (filters.empId && filters.empId > 0) params.emp_id = filters.empId;
    if (filters.fromEmpId && filters.fromEmpId > 0) params.from_emp_id = filters.fromEmpId;
    if (filters.tillEmpId && filters.tillEmpId > 0) params.till_emp_id = filters.tillEmpId;
    if (filters.empGroupId && filters.empGroupId > 0) params.emp_group_id = filters.empGroupId;
    if (filters.gender && filters.gender !== 'All') params.gender = filters.gender;
    if (filters.search && filters.search.trim()) params.search = filters.search.trim();

    const { data } = await api.get('/payroll/salary-process/', { params });
    return {
      items: (data?.items || []).map(mapSalaryItem),
      total_count: data?.total_count ?? 0,
      total_gross: data?.total_gross ?? 0,
      total_deductions: data?.total_deductions ?? 0,
      total_net: data?.total_net ?? 0,
      total_pages: data?.total_pages ?? 1,
      page: data?.page ?? 1,
      page_size: data?.page_size ?? 20,
    };
  },

  /** Run salary process */
  runProcess: async (payload) => {
    const { data } = await api.post('/payroll/salary-process/run', {
      month: payload.month,
      year: payload.year,
      company_id: payload.companyId || 0,
      branch_id: payload.branchId || 0,
      dept_id: payload.deptId || 0,
      emp_id: payload.empId || 0,
      from_emp_id: payload.fromEmpId || 0,
      till_emp_id: payload.tillEmpId || 0,
    });
    return data;
  },
  processSalary: (payload) => salaryProcessApi.runProcess(payload),

  /** Rollback salary process */
  rollbackProcess: async (payload) => {
    const { data } = await api.post('/payroll/salary-process/rollback', {
      month: payload.month,
      year: payload.year,
      company_id: payload.companyId || 0,
      branch_id: payload.branchId || 0,
      dept_id: payload.deptId || 0,
    });
    return data;
  },
  rollbackSalary: (payload) => salaryProcessApi.rollbackProcess(payload),

  /** Fetch data for Predefined Reports */
  fetchReportData: async (reportKey, filters = {}) => {
    const params = {
      month: filters.month ?? new Date().getMonth() + 1,
      year: filters.year ?? new Date().getFullYear(),
      company_id: filters.companyId || 0,
      branch_id: filters.branchId || 0,
      dept_id: filters.deptId || 0,
      emp_id: filters.empId || 0,
    };
    const { data } = await api.get(`/payroll/salary-process/reports/${reportKey}`, { params });
    return data;
  },
};
